import { isEmpty } from "lodash/fp";

import { get, httpDelete, patch, post, put } from "@readai/common/api";
import type {
  ApiTranscriptOperation,
  CreateNotetakerSessionResponse,
  CreateSessionParams,
  CreateSessionResponse,
  SessionInfo,
  UpdateSessionInfoRequestBody,
  User,
} from "@readai/common/types";

import { deleteSessionACL } from "./acl";

export const getSessionById = async (
  sessionId: string,
): Promise<SessionInfo> => {
  return get(`/sessions/${sessionId}`, { withCreds: true });
};

export const createSession = async (
  params: CreateSessionParams,
): Promise<CreateSessionResponse> => {
  return post(
    "/sessions",
    {
      meetingId: params.meetingId,
      meetingInstanceId: params.meetingInstanceId,
      meetingPassword: params.meetingPassword,
      meetingPlatform: params.meetingPlatform,
      meetingUrl: params.meetingUrl,
      title: params.title,
      userEventGroupId: params.userEventGroupId,
      liveNotesEnabled: params.liveNotesEnabled,
    },
    { withCreds: true },
  );
};

export const createNotetakerSession = async (
  params: CreateSessionParams,
): Promise<CreateNotetakerSessionResponse> => {
  return post(
    "/notetaker_sessions",
    {
      meetingId: params.meetingId,
      meetingInstanceId: params.meetingInstanceId,
      meetingPassword: params.meetingPassword,
      meetingPlatform: params.meetingPlatform,
      meetingUrl: params.meetingUrl,
      title: params.title,
      autoCreate: true,
      notetaker: true,
    },
    { withCreds: true },
  );
};

export const endSession = async (sessionId: string): Promise<SessionInfo> => {
  return put(`/sessions/${sessionId}`, { state: "ended" }, { withCreds: true });
};

export const optOutOfSession = async (
  sessionId: string,
): Promise<SessionInfo> => {
  return put(
    `/sessions/${sessionId}`,
    {
      state: "ended",
      optOut: true,
    },
    { withCreds: true },
  );
};

export const updateSessionInfo = async (
  sessionId: string,
  body: UpdateSessionInfoRequestBody,
) => {
  // if empty body throw an error
  if (isEmpty(body)) {
    throw Error("must provide session update body");
  }
  return patch(`/sessions/${sessionId}`, body, { withCreds: true });
};

export const patchTranscript = async (
  sessionId: string,
  operations: ApiTranscriptOperation[],
): Promise<void> => {
  return patch(
    `/sessions/${sessionId}/transcript`,
    { operations },
    { withCreds: true },
  );
};

export const restoreTranscript = async (sessionId: string): Promise<void> => {
  return patch(
    `/sessions/${sessionId}/transcript/restore`,
    {},
    { withCreds: true },
  );
};

export const restoreNotes = async (sessionId: string): Promise<void> => {
  return post(
    `/sessions/${sessionId}/meeting-notes/restore`,
    {},
    { withCreds: true },
  );
};

export const removeReport = async (
  sessionId: string,
  userId: User["id"],
): Promise<void> =>
  deleteSessionACL(sessionId, {
    sessionId,
    entityId: userId,
    kind: "user",
  });

export const deleteReport = async (sessionId: string): Promise<void> =>
  httpDelete(`/sessions/${sessionId}`, { withCreds: true });
